import {
  CheckmarkIcon,
  ChevronDownIcon,
  InfoIIcon,
  ReloadIcon,
  WarningIcon,
  XIcon,
} from '@moia-dev/pace-icons'
import {
  Badge,
  Button,
  Checkbox,
  Heading,
  IconButton,
  LoadingIndicator,
  Text,
} from '@moia-dev/pace-web'
import type { ChangeEvent } from 'react'
import { useRef, useState } from 'react'
import type { RouteBuilderVtrDataQuery } from './TripEditor.hook'
import type { Waypoint } from './types'
import { VtrSelectionModal } from './VtrSelectionModal'

type CbData = NonNullable<RouteBuilderVtrDataQuery['vtrData']>['cbData'][number]

const VTR_ODD_TYPE_MAP: Record<string, string> = {
  Stop: 'STOP_SIGN',
  Yield: 'YIELD_SIGN',
  'No Entry': 'NO_ENTRY',
  'Pedestrian Crossing': 'PEDESTRIAN_CROSSING',
  'Railroad Crossing': 'RAILROAD_CROSSING',
  'No Turn on Red': 'NO_TURN_ON_RED',
  'One Way': 'ONE_WAY',
  Roundabout: 'ROUNDABOUT',
  'Traffic Light': 'TRAFFIC_LIGHT',
}

// targetNumPerElement is the total number of encounters; it can't be below the unique count.
export const getTotalEncounterTarget = (
  cb: Pick<CbData, 'numUniqueElements' | 'targetNumPerElement'>,
): number => Math.max(cb.numUniqueElements ?? 1, cb.targetNumPerElement ?? 1)

const getRequiredElementKinds = (cbData: CbData): string[] =>
  cbData.oddElementTypes
    .map(t => VTR_ODD_TYPE_MAP[t])
    .filter((k): k is string => k != null)

// Unique count: deduplicate by rounded coordinate (6dp), matching admt-web-app logic
const getUniqueOddCounts = (
  waypoints: Waypoint[],
  requiredKinds: string[],
): Record<string, number> => {
  const coordSets: Record<string, Set<string>> = {}
  for (const kind of requiredKinds) coordSets[kind] = new Set()
  for (const wp of waypoints) {
    if (!wp.oddElementType || wp.lat == null || wp.lng == null) continue
    if (!requiredKinds.includes(wp.oddElementType)) continue
    const lat = Math.round(wp.lat * 1_000_000) / 1_000_000
    const lng = Math.round(wp.lng * 1_000_000) / 1_000_000
    coordSets[wp.oddElementType]?.add(`${lat},${lng}`)
  }
  const counts: Record<string, number> = {}
  for (const kind of requiredKinds) counts[kind] = coordSets[kind]?.size ?? 0
  return counts
}

// Total count: all waypoints of the required kinds (no dedup)
const getTotalOddCounts = (
  waypoints: Waypoint[],
  requiredKinds: string[],
): Record<string, number> => {
  const counts: Record<string, number> = {}
  for (const kind of requiredKinds) counts[kind] = 0
  for (const wp of waypoints) {
    if (
      wp.oddElementType &&
      wp.lat != null &&
      requiredKinds.includes(wp.oddElementType)
    ) {
      counts[wp.oddElementType] = (counts[wp.oddElementType] ?? 0) + 1
    }
  }
  return counts
}

export const validateVtrRequirements = (
  vtrData: NonNullable<RouteBuilderVtrDataQuery['vtrData']>,
  waypoints: Waypoint[],
  metropolisName: string | undefined,
  selectedCbIndex?: number,
): {
  locationValid: boolean
  uniqueElementsValid: boolean
  totalElementsValid: boolean
  allValid: boolean
  passedCount: number
  totalCount: number
} => {
  const cbData =
    selectedCbIndex !== undefined
      ? vtrData.cbData.slice(selectedCbIndex, selectedCbIndex + 1)
      : vtrData.cbData

  const locationValid =
    metropolisName == null
      ? true
      : cbData.every(
          cb =>
            !cb.location ||
            metropolisName.toLowerCase().includes(cb.location.toLowerCase()),
        )

  const uniqueElementsValid = cbData.every(cb => {
    if (!cb.numUniqueElements) return true
    const kinds = getRequiredElementKinds(cb)
    const counts = getUniqueOddCounts(waypoints, kinds)
    const uniqueSelected = Object.values(counts).reduce((sum, c) => sum + c, 0)
    return uniqueSelected >= cb.numUniqueElements
  })

  const totalElementsValid = cbData.every(cb => {
    if (!cb.targetNumPerElement || !cb.numUniqueElements) return true
    const kinds = getRequiredElementKinds(cb)
    const counts = getTotalOddCounts(waypoints, kinds)
    const target = getTotalEncounterTarget(cb)
    const total = Object.values(counts).reduce((sum, c) => sum + c, 0)
    return total >= target
  })

  const checks = [locationValid, uniqueElementsValid, totalElementsValid]
  const passedCount = checks.filter(Boolean).length

  return {
    locationValid,
    uniqueElementsValid,
    totalElementsValid,
    allValid: locationValid && uniqueElementsValid && totalElementsValid,
    passedCount,
    totalCount: checks.length,
  }
}

type SimilarRoute = {
  id: string
  name: string
}

type VtrRequirementsProps = {
  vtrData: NonNullable<RouteBuilderVtrDataQuery['vtrData']>
  waypoints: Waypoint[]
  metropolisName: string | undefined
  selectedCbIndex?: number
  similarRoutes?: SimilarRoute[]
  feasibilityErrors?: Record<string, string>
  generating?: boolean
  generateInMapArea?: boolean
  onGenerateInMapAreaChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onAutoGenerate?: () => void
  onSimilarRouteClick?: (id: string) => void
  onClear: () => void
}

const TRANSITION_MS = 220

export const VtrRequirements = ({
  vtrData,
  waypoints,
  metropolisName,
  selectedCbIndex,
  similarRoutes = [],
  feasibilityErrors = {},
  generating = false,
  generateInMapArea = false,
  onGenerateInMapAreaChange,
  onAutoGenerate,
  onSimilarRouteClick,
  onClear,
}: VtrRequirementsProps) => {
  const [collapsed, setCollapsed] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const bodyRef = useRef<HTMLDivElement>(null)

  const {
    locationValid,
    uniqueElementsValid,
    totalElementsValid,
    passedCount,
    totalCount,
  } = validateVtrRequirements(
    vtrData,
    waypoints,
    metropolisName,
    selectedCbIndex,
  )

  const allValid = locationValid && uniqueElementsValid && totalElementsValid

  const cbData =
    selectedCbIndex !== undefined
      ? vtrData.cbData.slice(selectedCbIndex, selectedCbIndex + 1)
      : vtrData.cbData

  return (
    <>
      <div
        css={theme => ({
          borderBottom: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
          display: 'flex',
          flexDirection: 'column',
        })}
      >
        {/* Collapsible header */}
        <div
          role="button"
          tabIndex={0}
          css={theme => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: `${theme.space.Space2} ${theme.space.Space3}`,
            cursor: 'pointer',
            userSelect: 'none',
          })}
          onClick={() => setCollapsed(prev => !prev)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') setCollapsed(prev => !prev)
          }}
        >
          <div css={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div
              css={{
                display: 'flex',
                transition: 'transform 200ms ease',
                transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
              }}
            >
              <IconButton
                aria-label={
                  collapsed ? 'Expand requirements' : 'Collapse requirements'
                }
                size="compact"
                variant="plain"
                onClick={e => {
                  e.stopPropagation()
                  setCollapsed(prev => !prev)
                }}
                icon={<ChevronDownIcon />}
              />
            </div>
            <Heading size="sm">{vtrData.key}</Heading>
            <Badge
              label={`${passedCount}/${totalCount}`}
              variant={allValid ? 'success' : 'warning'}
              icon={allValid ? <CheckmarkIcon /> : <WarningIcon />}
            />
          </div>
          <div css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <IconButton
              aria-label="VTR details"
              size="compact"
              variant="plain"
              onClick={e => {
                e.stopPropagation()
                setShowInfo(true)
              }}
              icon={<InfoIIcon />}
            />
            <IconButton
              aria-label="Clear VTR"
              size="compact"
              variant="plain"
              onClick={e => {
                e.stopPropagation()
                onClear()
              }}
              icon={<XIcon />}
            />
          </div>
        </div>

        {/* Animated collapsible body — grid-template-rows trick for smooth height animation */}
        <div
          ref={bodyRef}
          css={{
            display: 'grid',
            gridTemplateRows: collapsed ? '0fr' : '1fr',
            transition: `grid-template-rows ${TRANSITION_MS}ms ease`,
          }}
        >
          <div
            css={{
              overflow: 'hidden',
              opacity: collapsed ? 0 : 1,
              transition: `opacity ${TRANSITION_MS}ms ease`,
            }}
          >
            <div
              css={theme => ({
                padding: `0 ${theme.space.Space3} ${theme.space.Space5}`,
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space.Space2,
              })}
            >
              {cbData.map((cb, i) => {
                const cbKey = cb.codebeamerTestcaseId || (selectedCbIndex ?? i)
                const kinds = getRequiredElementKinds(cb)
                const uniqueCounts = getUniqueOddCounts(waypoints, kinds)
                const totalCounts = getTotalOddCounts(waypoints, kinds)
                const uniqueSelected = Object.values(uniqueCounts).reduce(
                  (sum, c) => sum + c,
                  0,
                )
                const totalSelected = Object.values(totalCounts).reduce(
                  (sum, c) => sum + c,
                  0,
                )
                const totalTarget = getTotalEncounterTarget(cb)

                return (
                  <div
                    key={String(cbKey)}
                    css={theme => ({
                      display: 'flex',
                      flexDirection: 'column',
                      gap: theme.space.Space1,
                    })}
                  >
                    <RequirementRow
                      label={`Location: ${cb.location || 'any'}`}
                      met={locationValid}
                    />

                    {cb.numUniqueElements > 0 && (
                      <>
                        <RequirementRow
                          label={`Number of unique ${cb.oddElementTypes.join(', ')}: ${uniqueSelected}/${cb.numUniqueElements}`}
                          met={uniqueElementsValid}
                        />
                        {kinds.map(kind => {
                          const err = feasibilityErrors[kind]
                          if (!err) return null
                          return (
                            <div
                              key={kind}
                              css={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                paddingLeft: 20,
                              }}
                            >
                              <WarningIcon
                                css={theme => ({
                                  width: 12,
                                  height: 12,
                                  color: theme.semantic.ColorWarningDefault,
                                  flexShrink: 0,
                                })}
                              />
                              <Text
                                size="sm"
                                css={theme => ({
                                  color: theme.semantic.ColorWarningDefault,
                                  fontSize: 11,
                                })}
                              >
                                {kind}: {err}
                              </Text>
                            </div>
                          )
                        })}
                      </>
                    )}

                    {totalTarget > 0 && (
                      <RequirementRow
                        label={`Total number of ${cb.oddElementTypes.join(', ')}: ${totalSelected}/${totalTarget}`}
                        met={totalElementsValid}
                      />
                    )}
                  </div>
                )
              })}

              {similarRoutes.length > 0 && (
                <Text size="sm" color="reduced">
                  Similar routes:{' '}
                  {similarRoutes.map((route, idx) => (
                    <span key={route.id}>
                      <span
                        css={theme => ({
                          color: theme.semantic.ColorActionPrimaryDefault,
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        })}
                        onClick={() => onSimilarRouteClick?.(route.id)}
                      >
                        {route.name}
                      </span>
                      {idx < similarRoutes.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </Text>
              )}

              {onAutoGenerate && (
                <div
                  css={theme => ({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.space.Space2,
                    paddingTop: theme.space.Space3,
                  })}
                >
                  <Button
                    label={
                      generating ? 'Generating...' : 'Auto-Generate New Route'
                    }
                    variant="secondary"
                    size="compact"
                    icon={
                      generating ? (
                        <LoadingIndicator size={16} />
                      ) : (
                        <ReloadIcon />
                      )
                    }
                    onClick={onAutoGenerate}
                    disabled={generating}
                    css={{ width: '100%' }}
                  />
                  {onGenerateInMapAreaChange !== undefined && (
                    <Checkbox
                      label="Generate route in current map area"
                      checked={generateInMapArea}
                      onChange={onGenerateInMapAreaChange}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showInfo && (
        <VtrSelectionModal
          vtrData={vtrData}
          initialCbIndex={selectedCbIndex ?? 0}
          readOnly
          onConfirm={() => setShowInfo(false)}
          onCancel={() => setShowInfo(false)}
        />
      )}
    </>
  )
}

const RequirementRow = ({ label, met }: { label: string; met: boolean }) => (
  <div css={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    {met ? (
      <CheckmarkIcon
        css={theme => ({
          width: 14,
          height: 14,
          color: theme.semantic.ColorSuccessDefault,
          flexShrink: 0,
        })}
      />
    ) : (
      <WarningIcon
        css={theme => ({
          width: 14,
          height: 14,
          color: theme.semantic.ColorWarningDefault,
          flexShrink: 0,
        })}
      />
    )}
    <Text
      size="sm"
      css={theme => ({
        color: met
          ? theme.semantic.ColorSuccessDefault
          : theme.semantic.ColorContentDefault,
      })}
    >
      {label}
    </Text>
  </div>
)
